import { Component, computed, ElementRef, inject, viewChild } from "@angular/core";
import { Store } from "@ngrx/store";
import { IconsService } from "../../services/icons/icons.service";
import { AppUser, AppUserActions, AppUserSelectors } from "../../modules/shared/app-user";
import { distinctUntilChanged, filter, map, Observable, share, skip, withLatestFrom } from "rxjs";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Strings } from "src/strings/strings";
import { NavigationEnd, Router } from "@angular/router";
import { MatSidenav } from "@angular/material/sidenav";
import { DialogManagePasswordComponent } from "../dialog-manage-password/dialog-manage-password.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "../../../configuration";
import { WindowService } from "../../modules/shared/window";
import { MessageService } from "../../services/message/message.service";
import { SessionTimeoutService } from "../../services/session-timeout/session-timeout.service";
import { NavigationMenuService } from "../../navigation/navigation-menu.service";
import { NAVIGATION_MENU_ITEM_ID } from "../../navigation/navigation-menu.models";
import { AuthActions } from "../../auth/store/actions";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DIALOG_SIZES } from "../../modules/shared/dialogs";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    standalone: false,
})
export class AppComponent {
    private readonly globalStore = inject<Store<{ appUser: AppUser }>>(Store);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly router = inject(Router);
    private readonly sessionTimeout = inject(SessionTimeoutService);
    private readonly dialog = inject(MatDialog);
    private readonly windowService = inject(WindowService);

    readonly sidenav = viewChild.required<MatSidenav>("sidenavElement");
    private readonly navigationStart = viewChild.required<unknown, ElementRef<HTMLElement>>("navigationStart", {
        read: ElementRef,
    });
    private readonly mainContent = viewChild.required<unknown, ElementRef<HTMLElement>>("mainContent", {
        read: ElementRef,
    });
    readonly strings = Strings;
    readonly appTitle = `${this.strings.appLongName}${environment.appTitleSuffix}`;
    readonly isImpersonatingUser$ = this.globalStore.select(AppUserSelectors.isImpersonatingUser);
    readonly appUser$: Observable<AppUser | null | undefined> = this.globalStore.select(AppUserSelectors.user);
    private readonly userNavigationMenu = inject(NavigationMenuService).userNavigationMenu;
    // Profile is left out because the profile block directly above this list
    // already links there — listing it again would duplicate that link.
    readonly primaryNavigationMenu = computed(() =>
        this.userNavigationMenu().filter((item) => item.id !== NAVIGATION_MENU_ITEM_ID.PROFILE),
    );
    readonly hasNavigableContent = computed(() => this.userNavigationMenu().length > 0);
    readonly icons = inject(IconsService);
    readonly messageService = inject(MessageService);

    readonly isHandset$: Observable<boolean> = this.breakpointObserver
        .observe([Breakpoints.XSmall, Breakpoints.Small])
        .pipe(map((result) => result.matches));

    constructor() {
        // Share successful, fully resolved navigation stream for UI and WCAG 2.2 focus management below
        const routeNavigated$ = this.router.events.pipe(
            filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd),
            share(),
        );

        // UI: Update navigation-dependent UI after each successful route navigation.
        routeNavigated$.pipe(withLatestFrom(this.isHandset$), takeUntilDestroyed()).subscribe(([, isHandset]) => {
            if (isHandset) {
                this.sidenav().close();
            }
        });

        // WCAG 2.2: Preserve native focus on initial load and for query/fragment changes; only a new path restarts tab order.
        routeNavigated$
            .pipe(
                map((navigationEnd) => navigationEnd.urlAfterRedirects.split(/[?#]/)[0]),
                distinctUntilChanged(),
                skip(1),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.navigationStart().nativeElement.focus({ preventScroll: true }));

        this.sessionTimeout.start();
    }

    focusMainContentForAccessibility(event: Event): void {
        event.preventDefault();
        this.mainContent().nativeElement.focus();
    }

    handleItemActivated(): void {
        if (this.sidenav().mode !== "side") {
            this.sidenav().close();
        }
    }

    handleLogout(): void {
        this.globalStore.dispatch(AuthActions.logout());
    }

    stopImpersonating(): void {
        this.globalStore.dispatch(AppUserActions.setImpersonatingUserId({ impersonatingUserId: undefined }));
        this.windowService.reload();
    }

    managePassword(): void {
        this.dialog.open(DialogManagePasswordComponent, {
            ...DIALOG_SIZES.small,
            ariaModal: true,
        });
    }
}
